import { Injectable, inject } from '@angular/core';
import { FormBuilder, Validators, FormGroup } from '@angular/forms';
import { Wallet } from '../../models/wallet.model';
import { Trade } from '../../models/trade.model';
import { AccountTransfer } from '../../models/account-transfer';
import { Investment } from '../../models/investment.model';
import { Isin } from '../../models/isin.model';
import { IsinValue } from '../../models/isin-value.model';

// Regex derivate dalla tua classe FormUtilityService
const DECIMAL_2_DIGIT_REGEX = /^-?\d+(\.\d{1,2})?$/;
const DECIMAL_4_DIGIT_REGEX = /^-?\d+(\.\d{1,4})?$/;

@Injectable({
  providedIn: 'root'
})
export class FormFactoryService {
  private fb = inject(FormBuilder);

  // Validatori custom basati sulla tua logica
  private UNIT_10_DECIMAL2 = Validators.pattern(DECIMAL_2_DIGIT_REGEX);
  private UNIT_10_DECIMAL4 = Validators.pattern(DECIMAL_4_DIGIT_REGEX);

  /**
   * Crea il form per un Wallet
   */
  createWalletForm(data?: Wallet): FormGroup {
    return this.fb.group({
      finWalletId: [data?.finWalletId],
      shortDescription: [data?.shortDescription, [Validators.required, Validators.minLength(3), Validators.maxLength(50)]],
      description: [data?.description, [Validators.maxLength(1000)]],
      startDate: [data?.startDate , [Validators.required]],
      base: [data?.base ?? 0, [Validators.required, this.UNIT_10_DECIMAL2]],
      stateId: [data?.stateId ?? 1]
    });
  }

  /**
   * Crea il form per un Trade (Movimento)
   */
  createTradeForm(data?: Trade): FormGroup {
    const amountValidators = [Validators.required, this.UNIT_10_DECIMAL2];

    return this.fb.group({
      finTradeId: [data?.finTradeId],
      shortDescription: [data?.shortDescription, [Validators.required, Validators.minLength(3), Validators.maxLength(50)]],
      description: [data?.description, [Validators.maxLength(1000)]],
      amount: [data?.amount ?? 0, amountValidators],
      finWalletId: [data?.finWalletId, [Validators.required]],
      etiquetteId: [data?.etiquetteId, [Validators.required]],
      accountingDate: [data?.accountingDate ? new Date(data.accountingDate) : new Date(), [Validators.required]],
      finTradeTypeId: [data?.finTradeTypeId ?? 0 ], 
      finSourceTypeId: [data?.finSourceTypeId ?? 1] // Default MANUALE
    });
  }

  /**
   * Crea il form per un Giroconto (AccountTransfer)
   */
  createAccountTransferForm(data?: AccountTransfer): FormGroup {
    return this.fb.group({
      finWalletIdFrom: [data?.finWalletIdFrom, [Validators.required]],
      finWalletIdTo: [data?.finWalletIdTo, [Validators.required]],
      amount: [data?.amount ?? 0, [Validators.required, Validators.min(0.01), this.UNIT_10_DECIMAL2]],
      accountingDate: [data?.accountingDate ? new Date(data.accountingDate) : new Date(), [Validators.required]],
      description: [data?.description ?? 'Giroconto (commento di sistema)', [Validators.maxLength(1000)]]
    });
  }

  /**
   * Crea il form per un ISIN (Anagrafica Titolo)
   */
  createIsinForm(data?: Isin): FormGroup {
    return this.fb.group({
      finIsinId: [data?.finIsinId],
      isin: [data?.isin, [Validators.required, Validators.maxLength(12)]],
      shortDescription: [data?.shortDescription, [Validators.required, Validators.minLength(3), Validators.maxLength(50)]],
      isinType: [data?.isinType, [Validators.maxLength(50)]]
    });
  }

  /**
   * Crea il form per il valore storico di un ISIN
   */
  createIsinValueForm(data?: IsinValue): FormGroup {
    return this.fb.group({
      finIsinValueId: [data?.finIsinValueId],
      priceDate: [data?.priceDate ? new Date(data.priceDate) : new Date(), [Validators.required]],
      priceValue: [data?.priceValue ?? 0, [Validators.required, this.UNIT_10_DECIMAL4]]
    });
  }

  /**
   * Crea il form per un Investimento (Acquisto/Vendita)
   */
  createInvestmentForm(data?: Investment): FormGroup {
    return this.fb.group({
      finInvestmentId: [data?.finInvestmentId],
      shortDescription: [data?.shortDescription, [Validators.required, Validators.minLength(3), Validators.maxLength(50)]],
      description: [data?.description, [Validators.maxLength(1000)]],
      accountingDate: [data?.accountingDate ? new Date(data.accountingDate) : new Date(), [Validators.required]],
      finWalletId: [data?.finWalletId, [Validators.required]],
      quantity: [data?.quantity ?? 0, [this.UNIT_10_DECIMAL4]],
      purchaseValue: [data?.purchaseValue ?? 0, [Validators.required, this.UNIT_10_DECIMAL4]],
      commissionCost: [data?.commissionCost ?? 0, [Validators.min(0), this.UNIT_10_DECIMAL2]],
      contestualCredit: [data?.contestualCredit ?? false]
    });
  }
}